import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './OrderDetails.css';

const OrderDetails = () => {
  const [orderData, setOrderData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [editedProducts, setEditedProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [shippingType, setShippingType] = useState('standard');
  const [fastShippingRates, setFastShippingRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponReplacementModal, setShowCouponReplacementModal] = useState(false);
  const [pendingCouponValidation, setPendingCouponValidation] = useState(null);
  const { orderId } = useParams();
  const navigate = useNavigate();

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://gokulnair.com/new_project/api/order-details/${orderId}`);
      setOrderData(response.data);
      setEditedOrder(response.data.order);
      setEditedProducts(response.data.products || []);
      
      // Set shipping type based on delivery_type
      if (response.data.order.delivery_type === 'ups_ground') {
        setShippingType('standard');
      } else {
        setShippingType('fast');
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await axios.get('https://gokulnair.com/new_project/api/products');
      setAllProducts(response.data);
    } catch (err) {
      console.error('Error fetching all products:', err);
    }
  }, []);

  const fetchFastShippingRates = useCallback(async () => {
    if (shippingType === 'fast' && editedOrder) {
      try {
        setLoadingRates(true);
        const params = {
          toPostalCode: editedOrder.shipping_pincode,
          toState: editedOrder.shipping_state,
          toCity: editedOrder.shipping_city
        };
        
        const response = await axios.post('https://gokulnair.com/new_project/api/get-shipstation-rates', params);
        setFastShippingRates(response.data);
      } catch (err) {
        console.error('Error fetching shipping rates:', err);
        setFastShippingRates([]);
      } finally {
        setLoadingRates(false);
      }
    }
  }, [shippingType, editedOrder]);

  useEffect(() => {
    fetchOrderDetails();
    fetchAllProducts();
  }, [fetchOrderDetails, fetchAllProducts]);

  useEffect(() => {
    if (editing && shippingType === 'fast') {
      fetchFastShippingRates();
    }
  }, [fetchFastShippingRates, editing, shippingType]);

  const getPaymentTypeText = (type) => {
    switch (type) {
      case 0: return 'Zelle';
      case 1: return 'Cash App';
      case 2: return 'Stripe';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Confirmed by User';
      case 2: return 'Confirmed by Admin';
      case 3: return 'Rejected by Admin';
      case 4: return 'Rejected by User';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status) {
      case 0: return 'status-pending';
      case 1: return 'status-confirmed-user';
      case 2: return 'status-confirmed-admin';
      case 3: return 'status-rejected-admin';
      case 4: return 'status-rejected-user';
      default: return 'status-pending';
    }
  };

  const handleInputChange = (field, value) => {
    setEditedOrder(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear coupon validation when coupon code changes
    if (field === 'coupon_code') {
      setCouponValidation(null);
      setCouponError('');
    }
  };

  const handleShippingTypeChange = (type) => {
    setShippingType(type);
    if (type === 'standard') {
      setFastShippingRates([]);
      // Set delivery_type to "ups_ground" and shipping price to 12 for standard shipping
      setEditedOrder(prev => ({
        ...prev,
        delivery_type: 'ups_ground',
        delivery_price: '12'
      }));
    }
  };

  const handleShippingRateSelect = (rate) => {
    setEditedOrder(prev => ({
      ...prev,
      delivery_type: rate.serviceCode,
      delivery_price: rate.shipmentCost.toString()
    }));
  };

  const handleAddProduct = (product) => {
    // Check if product is already in the order
    const isAlreadyAdded = editedProducts.some(p => p.sku === product.sku);
    if (!isAlreadyAdded) {
      setEditedProducts(prev => [...prev, product]);
    }
  };

  const handleRemoveProduct = (sku) => {
    setEditedProducts(prev => prev.filter(p => p.sku !== sku));
  };

  const handleProductQuantityChange = (sku, newQuantity) => {
    if (newQuantity < 1) return; // Don't allow quantity less than 1
    
    setEditedProducts(prev => 
      prev.map(product => 
        product.sku === sku 
          ? { ...product, qty: newQuantity }
          : product
      )
    );
  };

  const isProductInOrder = (sku) => {
    return editedProducts.some(p => p.sku === sku);
  };

  const filteredProducts = allProducts.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate products total (excluding shipping)
  const calculateProductsTotal = () => {
    return currentProducts.reduce((sum, product) => {
      return sum + (parseFloat(product.price) * (product.qty || 1));
    }, 0);
  };

  // Calculate total
  const calculateTotal = () => {
    const productsTotal = calculateProductsTotal();
    const shippingCost = parseFloat(currentOrder.delivery_price) || 0;
    return productsTotal + shippingCost;
  };

  // Validate coupon code
  const validateCoupon = async (couponCode) => {
    if (!couponCode || !couponCode.trim()) {
      setCouponValidation(null);
      setCouponError('');
      return;
    }

    try {
      setValidatingCoupon(true);
      setCouponError('');
      
      const response = await axios.post('https://gokulnair.com/new_project/api/check-coupon', {
        code: couponCode.trim(),
        email: currentOrder.email,
        purchase_amount: calculateProductsTotal()
      });

      if (response.data.valid) {
        // Check if there's already a coupon applied
        if (currentOrder.coupon) {
          // Show replacement modal
          setPendingCouponValidation(response.data);
          setShowCouponReplacementModal(true);
        } else {
          // No existing coupon, apply directly
          setCouponValidation(response.data);
          setCouponError('');
        }
      } else {
        setCouponValidation(null);
        setCouponError('Invalid coupon code');
      }
    } catch (err) {
      console.error('Error validating coupon:', err);
      setCouponValidation(null);
      setCouponError('Error validating coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Handle coupon replacement choice
  const handleCouponReplacement = (replaceExisting) => {
    if (replaceExisting) {
      // Replace existing coupon with new one
      setCouponValidation(pendingCouponValidation);
      setCouponError('');
    } else {
      // Add new coupon to existing (stack them)
      setCouponValidation(pendingCouponValidation);
      setCouponError('');
    }
    setShowCouponReplacementModal(false);
    setPendingCouponValidation(null);
  };

  // Cancel coupon replacement
  const cancelCouponReplacement = () => {
    setShowCouponReplacementModal(false);
    setPendingCouponValidation(null);
  };

  // Calculate total with coupon discount
  const calculateTotalWithCoupon = () => {
    const subtotal = calculateTotal();
    let totalDiscount = 0;
    
    // Calculate discount from existing coupon
    if (currentOrder.coupon) {
      const existingDiscountValue = parseFloat(currentOrder.coupon.discount_value);
      if (currentOrder.coupon.discount_type === 'percentage') {
        totalDiscount += (subtotal * existingDiscountValue) / 100;
      } else {
        totalDiscount += existingDiscountValue;
      }
    }
    
    // Calculate discount from new coupon (if replacing, this will override)
    if (couponValidation && couponValidation.valid) {
      const newDiscountValue = parseFloat(couponValidation.discount_value);
      if (couponValidation.discount_type === 'percentage') {
        // If replacing, use new discount; if adding, add to existing
        if (currentOrder.coupon) {
          // For now, we'll replace the discount calculation
          // This can be enhanced based on business logic
          totalDiscount = (subtotal * newDiscountValue) / 100;
        } else {
          totalDiscount += (subtotal * newDiscountValue) / 100;
        }
      } else {
        if (currentOrder.coupon) {
          totalDiscount = newDiscountValue;
        } else {
          totalDiscount += newDiscountValue;
        }
      }
    }
    
    return Math.max(0, subtotal - totalDiscount);
  };

  const handleSave = async () => {
    try {
      // Prepare SKU string from products
      const skuString = editedProducts.map(product => product.sku).join(',');
      
      // Prepare QTY string from products (matching SKU order)
      const qtyString = editedProducts.map(product => product.qty || 1).join(',');
      
      // Prepare API parameters
      const apiParams = {
        name: editedOrder.name,
        email: editedOrder.email,
        phone: editedOrder.phone,
        sku: skuString,
        qty: qtyString,
        shipping_address: editedOrder.shipping_address,
        shipping_city: editedOrder.shipping_city,
        shipping_state: editedOrder.shipping_state,
        shipping_pincode: editedOrder.shipping_pincode,
        payment_type: editedOrder.payment_type,
        delivery_type: editedOrder.delivery_type,
        delivery_price: editedOrder.delivery_price,
        coupon_code: editedOrder.coupon_code || '',
        coupon_id: couponValidation?.id || editedOrder.coupon?.id || null
      };

      console.log('Sending API request with params:', apiParams);

      // Make API call to update order
      const response = await axios.post(`https://gokulnair.com/new_project/api/order/update/${editedOrder.order_id}`, {
        ...apiParams,
        payment_status: editedOrder.payment_status
      });
      
      if (response.data.message === 'Order updated successfully') {
        console.log('Order updated successfully:', response.data);
        // Update the order data with edited values
        setOrderData(prev => ({
          ...prev,
          order: editedOrder,
          products: editedProducts
        }));
        setEditing(false);
        setShowProductSelector(false);
        setSearchTerm('');
        setShippingType('standard');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000); // Hide after 5 seconds
      } else {
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
    }
  };

  const handleCancel = () => {
    setEditedOrder(orderData.order);
    setEditedProducts(orderData.products || []);
    setEditing(false);
    setShowProductSelector(false);
    setSearchTerm('');
    setShippingType('standard');
    setFastShippingRates([]);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading order details...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="order-details-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  if (!orderData || !orderData.order) {
    return (
      <Layout>
        <div className="order-details-content">
          <div className="no-order">Order not found</div>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  const { order, products } = orderData;
  const currentOrder = editing ? editedOrder : order;
  const currentProducts = editing ? editedProducts : products;

  return (
    <Layout>
      <div className="order-details-content">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h3>Order Updated Successfully!</h3>
              <p>Order ID: {currentOrder.order_id}</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowSuccessMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        {/* Error Message */}
        {showErrorMessage && (
          <div className="error-message-box">
            <div className="error-icon">❌</div>
            <div className="error-content">
              <h3>Update Failed</h3>
              <p>Failed to update order. Please try again.</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowErrorMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        <header className="order-details-header">
          <div className="header-left">
            <h1>Order Details</h1>
            <p>Order ID: {currentOrder.order_id}</p>
          </div>
          <div className="header-actions">
            {editing ? (
              <>
                <button onClick={handleSave} className="save-button">
                  Save Changes
                </button>
                <button onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
              </>
            ) : (
              currentOrder.payment_status === 2 ? (
                <button className="edit-button-disabled" disabled>
                  Edit Order (Confirmed by Admin)
                </button>
              ) : (
                <button onClick={() => setEditing(true)} className="edit-button">
                  Edit Order
                </button>
              )
            )}
            <button onClick={() => navigate('/dashboard')} className="back-button">
              Back to Dashboard
            </button>
          </div>
        </header>

        <main className="order-details-main">
          <div className="order-card">
            <div className="order-section">
              <h2>Customer Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name:</label>
                  {editing ? (
                    <input
                      type="text"
                      value={currentOrder.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.name}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  {editing ? (
                    <input
                      type="email"
                      value={currentOrder.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.email}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  {editing ? (
                    <input
                      type="tel"
                      value={currentOrder.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.phone}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="order-section">
              <h2>Shipping Address</h2>
              <div className="address-info">
                {editing ? (
                  <div className="address-edit">
                    <input
                      type="text"
                      value={currentOrder.shipping_address}
                      onChange={(e) => handleInputChange('shipping_address', e.target.value)}
                      className="edit-input"
                      placeholder="Shipping Address"
                    />
                    <div className="address-row">
                      <input
                        type="text"
                        value={currentOrder.shipping_city}
                        onChange={(e) => handleInputChange('shipping_city', e.target.value)}
                        className="edit-input"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={currentOrder.shipping_state}
                        onChange={(e) => handleInputChange('shipping_state', e.target.value)}
                        className="edit-input"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={currentOrder.shipping_pincode}
                        onChange={(e) => handleInputChange('shipping_pincode', e.target.value)}
                        className="edit-input"
                        placeholder="Pincode"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p>{currentOrder.shipping_address}</p>
                    <p>{currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_pincode}</p>
                  </>
                )}
              </div>
            </div>

            <div className="order-section">
              <div className="section-header">
                <h2>Products ({currentProducts.length})</h2>
                {editing && (
                  <button 
                    onClick={() => setShowProductSelector(!showProductSelector)}
                    className="add-product-button"
                  >
                    {showProductSelector ? 'Hide Product Selector' : 'Add Products'}
                  </button>
                )}
              </div>

              {editing && showProductSelector && (
                <div className="product-selector">
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search products by name or SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <div className="products-grid">
                    {filteredProducts.map((product) => (
                      <div 
                        key={product.id} 
                        className={`product-selector-item ${isProductInOrder(product.sku) ? 'selected' : ''}`}
                        onClick={() => handleAddProduct(product)}
                      >
                        <div className="product-selector-image">
                          <img 
                            src={product.image_src} 
                            alt={product.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="image-fallback" style={{ display: 'none' }}>
                            {product.title}
                          </div>
                        </div>
                        <div className="product-selector-details">
                          <h4>{product.title}</h4>
                          <p className="product-sku">SKU: {product.sku}</p>
                          <p className="product-price">${product.price}</p>
                          {isProductInOrder(product.sku) && (
                            <span className="added-badge">✓ Added</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="products-list-simple">
                {currentProducts && currentProducts.length > 0 ? (
                  currentProducts.map((product, index) => (
                    <div key={product.id || index} className="product-item-simple">
                      <div className="product-info">
                        <span className="product-title">{product.title}</span>
                        <span className="product-sku">SKU: {product.sku}</span>
                        <span className="product-price">${product.price}</span>
                        <span className="product-quantity">
                          Qty: {editing ? (
                            <input
                              type="number"
                              min="1"
                              value={product.qty || 1}
                              onChange={(e) => handleProductQuantityChange(product.sku, parseInt(e.target.value))}
                              className="quantity-input"
                            />
                          ) : (
                            product.qty || 1
                          )}
                        </span>
                        <span className="product-total">
                          Total: ${((parseFloat(product.price) || 0) * (product.qty || 1)).toFixed(2)}
                        </span>
                      </div>
                      {editing && (
                        <button 
                          onClick={() => handleRemoveProduct(product.sku)}
                          className="remove-product-button-simple"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-products">
                    <p>No products found for this order.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="order-section">
              <h2>Order Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Coupon Code:</label>
                  {editing ? (
                    <div className="coupon-input-container">
                      {/* Show current coupon if exists */}
                      {currentOrder.coupon && (
                        <div className="current-coupon-display">
                          <div className="current-coupon-box">
                            <span className="current-coupon-label">Current Coupon:</span>
                            <span className="current-coupon-code">{currentOrder.coupon.code}</span>
                            <span className="current-coupon-discount">
                              {currentOrder.coupon.discount_type === 'percentage' 
                                ? `${currentOrder.coupon.discount_value}% off`
                                : `$${currentOrder.coupon.discount_value} off`
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="coupon-input-row">
                        <input
                          type="text"
                          value={currentOrder.coupon_code || ''}
                          onChange={(e) => handleInputChange('coupon_code', e.target.value)}
                          className={`edit-input ${couponError ? 'error' : couponValidation?.valid ? 'valid' : ''}`}
                          placeholder="Enter new coupon code"
                        />
                        <button
                          type="button"
                          onClick={() => validateCoupon(currentOrder.coupon_code)}
                          disabled={validatingCoupon || !currentOrder.coupon_code}
                          className="validate-coupon-btn"
                        >
                          {validatingCoupon ? 'Validating...' : 'Validate'}
                        </button>
                      </div>
                      {validatingCoupon && (
                        <div className="coupon-loading">Validating coupon...</div>
                      )}
                      {couponError && (
                        <div className="coupon-error">{couponError}</div>
                      )}
                      {couponValidation?.valid && (
                        <div className="coupon-success">
                          ✓ Valid coupon: {couponValidation.discount_type === 'percentage' 
                            ? `${couponValidation.discount_value}% off`
                            : `$${couponValidation.discount_value} off`
                          }
                        </div>
                      )}
                    </div>
                  ) : currentOrder.coupon ? (
                    <div className="coupon-info">
                      <span className="coupon-code">{currentOrder.coupon.code}</span>
                      <span className="coupon-discount">
                        {currentOrder.coupon.discount_type === 'percentage' 
                          ? `${currentOrder.coupon.discount_value}% off`
                          : `$${currentOrder.coupon.discount_value} off`
                        }
                      </span>
                    </div>
                  ) : (
                    <span>No coupon applied</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Order ID:</label>
                  <span>{currentOrder.order_id}</span>
                </div>
                <div className="info-item">
                  <label>SKU:</label>
                  {editing ? (
                    <input
                      type="text"
                      value={currentOrder.sku}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.sku}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Shipping Type:</label>
                  {editing ? (
                    <div className="shipping-options">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="shippingType"
                          value="standard"
                          checked={shippingType === 'standard'}
                          onChange={(e) => handleShippingTypeChange(e.target.value)}
                        />
                        <span>Standard Shipping</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="shippingType"
                          value="fast"
                          checked={shippingType === 'fast'}
                          onChange={(e) => handleShippingTypeChange(e.target.value)}
                        />
                        <span>Fast Shipping</span>
                      </label>
                    </div>
                  ) : (
                    <span>{currentOrder.delivery_type}</span>
                  )}
                </div>
                {editing && shippingType === 'fast' && (
                  <div className="info-item">
                    <label>Fast Shipping Rates:</label>
                    <div className="shipping-rates">
                      {loadingRates ? (
                        <div className="loading-rates">Loading rates...</div>
                      ) : fastShippingRates.length > 0 ? (
                        <div className="rates-list">
                          {fastShippingRates.map((rate, index) => (
                            <div 
                              key={index}
                              className={`rate-option ${currentOrder.delivery_type === rate.serviceCode ? 'selected' : ''}`}
                              onClick={() => handleShippingRateSelect(rate)}
                            >
                              <div className="rate-info">
                                <span className="rate-name">{rate.serviceName}</span>
                                <span className="rate-price">${rate.shipmentCost}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-rates">No rates available</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <label>Shipping Price:</label>
                  {editing ? (
                    <input
                      type="number"
                      step="0.01"
                      value={currentOrder.delivery_price}
                      onChange={(e) => handleInputChange('delivery_price', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>${currentOrder.delivery_price}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Payment Type:</label>
                  {editing ? (
                    <select
                      value={currentOrder.payment_type}
                      onChange={(e) => handleInputChange('payment_type', parseInt(e.target.value))}
                      className="edit-select"
                    >
                      <option value={0}>Zelle</option>
                      <option value={1}>Cash App</option>
                    </select>
                  ) : (
                    <span className="payment-type-badge">
                      {getPaymentTypeText(currentOrder.payment_type)}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>Payment Status:</label>
                  {editing ? (
                    <select
                      value={currentOrder.payment_status}
                      onChange={(e) => handleInputChange('payment_status', parseInt(e.target.value))}
                      className="edit-select"
                    >
                      <option value={0}>Pending</option>
                      <option value={1}>Confirmed by User</option>
                      <option value={2}>Confirmed by Admin</option>
                      <option value={3}>Rejected by Admin</option>
                      <option value={4}>Rejected by User</option>
                    </select>
                  ) : (
                    <span className={`status-badge ${getPaymentStatusClass(currentOrder.payment_status)}`}>
                      {getPaymentStatusText(currentOrder.payment_status)}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>Created Date:</label>
                  <span>{new Date(currentOrder.created_at).toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <label>Products Total:</label>
                  <span>${calculateProductsTotal().toFixed(2)}</span>
                </div>
                <div className="info-item">
                  <label>Shipping Cost:</label>
                  <span>${currentOrder.delivery_price}</span>
                </div>
                {couponValidation?.valid && (
                  <div className="info-item">
                    <label>Coupon Discount:</label>
                    <span className="coupon-discount-amount">
                      -${(calculateTotal() - calculateTotalWithCoupon()).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="info-item total-item">
                  <label>Total:</label>
                  <span className="total-amount">${calculateTotalWithCoupon().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Coupon Replacement Modal */}
      {showCouponReplacementModal && (
        <div className="modal-overlay">
          <div className="modal-content coupon-replacement-modal">
            <div className="modal-header">
              <h3>Coupon Replacement</h3>
            </div>
            <div className="modal-body">
              <p>You already have a coupon applied:</p>
              <div className="existing-coupon-info">
                <span className="existing-coupon-code">{currentOrder.coupon.code}</span>
                <span className="existing-coupon-discount">
                  {currentOrder.coupon.discount_type === 'percentage' 
                    ? `${currentOrder.coupon.discount_value}% off`
                    : `$${currentOrder.coupon.discount_value} off`
                  }
                </span>
              </div>
              <p>New coupon: <strong>{pendingCouponValidation?.code}</strong></p>
              <p>What would you like to do?</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel"
                onClick={cancelCouponReplacement}
              >
                Cancel
              </button>
              <button 
                className="modal-btn modal-btn-confirm"
                onClick={() => handleCouponReplacement(true)}
              >
                Replace Existing Coupon
              </button>
              <button 
                className="modal-btn modal-btn-confirm"
                onClick={() => handleCouponReplacement(false)}
              >
                Add New Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default OrderDetails; 